//TODO : Refactor after ai
/**
 * Rahmen der Viacid-Anwendung: Seitenleiste mit den Objektklassen,
 * gegliedert nach Vorgängen, Dokumenten und Stammdaten (Reihenfolge nach der
 * Priorisierung im Datenmodell), oben Brotkrumen, „Ansicht als“ (Rolle),
 * Zurücksetzen und Abmelden. Unter 900 px wird die Seitenleiste zum Menü.
 *
 * Positionen und Zeilen stehen nicht in der Navigation – sie sind nur auf der
 * Detailseite ihres Kopfes zu sehen.
 */

import { useEffect, useState, type ReactNode } from "react";
import {
  FileText,
  FolderOpen,
  HardHat,
  Home,
  LogOut,
  Menu,
  RotateCcw,
  Smartphone,
  Workflow,
  X,
} from "lucide-react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { MAIN_FEATURES } from "@app/router/constants";
import { useDatenStore } from "@app/store";
import { useLogout } from "@features/auth";
import {
  AppShell,
  Breadcrumbs,
  Button,
  CountBadge,
  NavSectionLabel,
  Sidebar,
  SidebarMenuItem,
  type Crumb,
} from "@shared/components/widgets/ooux";
import {
  NAVIGATIONSBEREICHE,
  alleRollen,
  datensaetze,
  datensatz,
  klassenImBereich,
  objektKlasse,
} from "@shared/demo-data/viacid";
import { useWindowSize } from "@shared/hooks";
import { useTranslation } from "@shared/locales";
import "./viacid.css";

const BEREICH_ICON = {
  vorgaenge: <Workflow size={18} />,
  dokumente: <FileText size={18} />,
  stammdaten: <FolderOpen size={18} />,
} as const;

const MOBIL_AB = 900;

interface ViacidShellProps {
  children: ReactNode;
}

export function ViacidShell({ children }: ViacidShellProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useLogout();
  const { width } = useWindowSize();
  const mobil = width > 0 && width < MOBIL_AB;
  const [menuOffen, setMenuOffen] = useState(false);

  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const setRolle = useDatenStore((s) => s.setRolle);
  const zuruecksetzen = useDatenStore((s) => s.zuruecksetzen);
  const meldung = useDatenStore((s) => s.meldung);
  const meldungSchliessen = useDatenStore((s) => s.meldungSchliessen);

  useEffect(() => {
    if (!meldung) return;
    const timer = setTimeout(meldungSchliessen, meldung.fehler ? 9000 : 6000);
    return () => clearTimeout(timer);
  }, [meldung, meldungSchliessen]);

  const mitId = matchPath(`/${MAIN_FEATURES.Objekte}/:slug/:id`, pathname);
  const ohneId = matchPath(`/${MAIN_FEATURES.Objekte}/:slug`, pathname);
  const slug = mitId?.params.slug ?? ohneId?.params.slug;
  const id = mitId?.params.id;
  const klasse = slug ? objektKlasse(slug) : undefined;
  const satz = klasse && id ? datensatz(bestand, klasse.slug, id) : undefined;

  // Navigieren schliesst das mobile Menü
  const gehe = (pfad: string) => {
    setMenuOffen(false);
    navigate(pfad);
  };
  const crumbs: Crumb[] = [
    { label: t("viacid.start"), onClick: () => gehe(PagesRoutes.home.index) },
  ];
  if (pathname === PagesRoutes.baustelle.index)
    crumbs.push({ label: t("viacid.baustelle.titel") });
  if (klasse)
    crumbs.push({
      label: klasse.plural,
      onClick: () => gehe(PagesRoutes.objekte.show(klasse.slug)),
    });
  if (satz) crumbs.push({ label: satz.kennung ?? satz.titel });
  const sichtbareCrumbs = mobil ? crumbs.slice(-1) : crumbs;
  const schmal = width > 0 && width <= 1120;
  const kopfLinks = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
        width: "100%",
      }}
    >
      {mobil && (
        <Button
          variant="ghost"
          color="neutral"
          size="sm"
          iconOnly
          leadingIcon={<Menu size={18} />}
          onClick={() => setMenuOffen(true)}
          aria-label={t("viacid.menu")}
        />
      )}
      <Breadcrumbs items={sichtbareCrumbs} />
    </div>
  );

  const sidebar = (
    <Sidebar
      style={
        mobil
          ? {
              position: "fixed",
              inset: "0 auto 0 0",
              zIndex: 60,
              boxShadow: "0 0 0 100vmax rgba(16,24,40,.35)",
            }
          : undefined
      }
      brand={
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <strong
              style={{
                font: "var(--t-subtitle)",
                color: "var(--text-primary)",
              }}
            >
              Viacid
            </strong>
            <span
              style={{
                font: "var(--t-paragraph-s)",
                color: "var(--text-tertiary)",
              }}
            >
              {t("viacid.prototyp")}
            </span>
          </div>
          {mobil && (
            <Button
              variant="ghost"
              color="neutral"
              size="sm"
              iconOnly
              leadingIcon={<X size={18} />}
              onClick={() => setMenuOffen(false)}
              aria-label={t("viacid.schliessen")}
            />
          )}
        </div>
      }
    >
      <SidebarMenuItem
        icon={<Home size={18} />}
        label={t("viacid.start")}
        active={pathname === PagesRoutes.home.index}
        onClick={() => gehe(PagesRoutes.home.index)}
      />
      <SidebarMenuItem
        icon={<HardHat size={18} />}
        label={t("viacid.baustelle.titel")}
        active={pathname === PagesRoutes.baustelle.index}
        onClick={() => gehe(PagesRoutes.baustelle.index)}
      />
      {NAVIGATIONSBEREICHE.map((bereich) => (
        <div
          key={bereich.key}
          style={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <NavSectionLabel>
            {t(`viacid.bereiche.${bereich.key}`)}
          </NavSectionLabel>
          {klassenImBereich(bereich.key).map((k) => (
            <SidebarMenuItem
              key={k.slug}
              icon={
                k.mobil ? <Smartphone size={16} /> : BEREICH_ICON[bereich.key]
              }
              label={k.plural}
              active={slug === k.slug}
              badge={
                <CountBadge
                  count={datensaetze(bestand, k.slug).length}
                  variant="light"
                  color="neutral"
                />
              }
              onClick={() => gehe(PagesRoutes.objekte.show(k.slug))}
            />
          ))}
        </div>
      ))}
    </Sidebar>
  );

  return (
    <AppShell
      style={{ height: "100vh" }}
      contentPadding={mobil ? 12 : undefined}
      sidebar={!mobil || menuOffen ? sidebar : undefined}
      // Das UI-Kit blendet die linke Kopfzeile unter 1120 px aus – darunter stehen Menü und Brotkrumen in der Mitte.
      search={schmal ? kopfLinks : undefined}
      barLeft={schmal ? undefined : kopfLinks}
      barRight={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {!mobil && (
            <label
              style={{
                font: "var(--t-paragraph-s)",
                color: "var(--text-tertiary)",
              }}
              htmlFor="ansicht-als"
            >
              {t("viacid.ansichtAls")}
            </label>
          )}
          <select
            id="ansicht-als"
            value={rolle}
            onChange={(e) => setRolle(e.target.value)}
            className="bui-input"
            aria-label={t("viacid.ansichtAls")}
            style={{
              height: 36,
              padding: "0 10px",
              font: "var(--t-paragraph-s)",
              maxWidth: mobil ? 150 : 260,
            }}
          >
            <optgroup label={t("viacid.rollen.intern")}>
              {alleRollen()
                .filter((r) => r.intern)
                .map((r) => (
                  <option key={r.kuerzel} value={r.kuerzel}>
                    {r.name}
                  </option>
                ))}
            </optgroup>
            <optgroup label={t("viacid.rollen.extern")}>
              {alleRollen()
                .filter((r) => !r.intern)
                .map((r) => (
                  <option key={r.kuerzel} value={r.kuerzel}>
                    {r.name}
                  </option>
                ))}
            </optgroup>
          </select>
          <Button
            variant="ghost"
            color="neutral"
            size="sm"
            iconOnly={mobil}
            leadingIcon={<RotateCcw size={16} />}
            onClick={zuruecksetzen}
            title={t("viacid.zuruecksetzenHinweis")}
            aria-label={t("viacid.zuruecksetzen")}
          >
            {t("viacid.zuruecksetzen")}
          </Button>
          {!mobil && (
            <Button
              variant="ghost"
              color="neutral"
              size="sm"
              leadingIcon={<LogOut size={16} />}
              onClick={logout}
            >
              {t("viacid.abmelden")}
            </Button>
          )}
        </div>
      }
    >
      {meldung && (
        <div
          role="status"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            marginBottom: 16,
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            padding: "12px 16px",
            borderRadius: 12,
            background: meldung.fehler
              ? "var(--error-50, #fef3f2)"
              : "var(--success-50, #ecfdf3)",
            color: meldung.fehler
              ? "var(--error-700, #b42318)"
              : "var(--success-700, #067647)",
            boxShadow: "inset 0 0 0 1px currentColor",
            font: "var(--t-paragraph-s)",
          }}
        >
          <span style={{ flex: 1 }}>{meldung.text}</span>
          <button
            type="button"
            onClick={meldungSchliessen}
            aria-label={t("viacid.schliessen")}
            style={{
              background: "none",
              border: 0,
              cursor: "pointer",
              color: "inherit",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      {children}
    </AppShell>
  );
}
